export const DOCS_STRUCTURE = [
  {
    id: "getting-started",
    title: "Getting Started",
    articles: [
      {
        id: "introduction",
        title: "Introduction",
        content: `
# Introduction to Flare

Flare is a comprehensive video recording and editing platform built for macOS that enables you to record screen content, camera feeds, and audio simultaneously.

## What you can do with Flare

- **Record your screen** — Capture your entire screen, specific windows, or custom regions
- **Add camera overlay** — Include your webcam as a picture-in-picture overlay
- **Capture audio** — Record system audio and microphone simultaneously
- **Edit in real-time** — Use our powerful timeline editor to perfect your videos
- **Share instantly** — Generate shareable links or download your videos

## Who is Flare for?

Flare is designed for creators, educators, and professionals who need to create high-quality video content quickly:

- Product demos and tutorials
- Educational content and courses
- Team updates and async communication
- Bug reports and technical walkthroughs
- Sales and marketing videos

## Next steps

Ready to get started? Here's what to do next:

1. [Download and install Flare](/help?article=download-install)
2. [Create your first recording](/help?article=first-recording)
3. [Learn the editing basics](/help?article=editor-overview)
        `,
      },
      {
        id: "download-install",
        title: "Download & Installation",
        content: `
# Download & Installation

Get Flare up and running on your Mac in minutes.

## System requirements

Before downloading, make sure your Mac meets these requirements:

| Requirement | Minimum | Recommended |
|-------------|---------|-------------|
| macOS | 14.0 (Sonoma) | 15.0+ (Sequoia) |
| Processor | Apple Silicon or Intel | Apple Silicon (M1/M2/M3) |
| RAM | 4 GB | 8 GB+ |
| Storage | 1 GB | 2 GB+ free space |

## Download Flare

1. Visit [flare.link](https://flare.link)
2. Click the **Download** button
3. The DMG file will download to your Downloads folder

## Install the app

1. Open the downloaded \`.dmg\` file
2. Drag **Flare** to your **Applications** folder
3. Eject the DMG from your desktop or Finder sidebar

## First launch

When you first open Flare, macOS will ask for permissions:

### Screen Recording
Required to capture your screen content.
\`System Settings → Privacy & Security → Screen Recording → Enable Flare\`

### Microphone
Required to record audio from your microphone.
\`System Settings → Privacy & Security → Microphone → Enable Flare\`

### Camera
Required for webcam overlay.
\`System Settings → Privacy & Security → Camera → Enable Flare\`

## Sign in

After granting permissions:
1. Click **Sign In** or **Create Account**
2. Enter your credentials
3. You're ready to record!

## Troubleshooting

**App won't open?**
Right-click the app and select "Open" to bypass Gatekeeper on first launch.

**Permissions not working?**
Try removing Flare from the permissions list and re-adding it, then restart the app.
        `,
      },
      {
        id: "first-recording",
        title: "Your First Recording",
        content: `
# Your First Recording

Create your first video with Flare in under 2 minutes.

## Quick start

### 1. Open Flare
Launch Flare from your Applications folder or Dock.

### 2. Choose what to record
Click the capture mode selector to choose:
- **Full Screen** — Records everything on your display
- **Window** — Records a specific application window
- **Region** — Records a custom area you select

### 3. Configure your setup
Before recording, configure:
- **Camera** — Toggle on/off, choose position
- **Microphone** — Select input device, adjust levels
- **System Audio** — Capture sounds from your computer

### 4. Start recording
Click the red **Record** button or press the keyboard shortcut. A 3-second countdown gives you time to prepare.

### 5. Stop recording
When finished:
- Click the **Stop** button in the menu bar
- Or use the keyboard shortcut

### 6. Review and share
Your recording opens automatically. From here you can:
- **Edit** — Trim, cut, and enhance
- **Share** — Get a link to share instantly
- **Download** — Save the video file locally

## Tips for great recordings

**Prepare your screen**
- Close unnecessary apps and notifications
- Hide sensitive information
- Arrange windows before recording

**Audio quality**
- Use a quiet environment
- Position your microphone consistently
- Do a test recording to check levels

**Camera presence**
- Ensure good lighting on your face
- Position camera at eye level
- Check your background

**Practice first**
Do a quick practice run before your final recording. It's much easier than re-recording!
        `,
      },
    ],
  },
  {
    id: "recording",
    title: "Recording",
    articles: [
      {
        id: "capture-modes",
        title: "Capture Modes",
        content: `
# Capture Modes

Flare offers three ways to capture your screen, each suited for different use cases.

## Full Screen

Captures everything visible on your display.

**Best for:**
- Software tutorials
- Presentations
- General screencasts

**How to use:**
1. Select **Full Screen** mode
2. If you have multiple monitors, choose which one to record
3. Click Record

## Window Capture

Records only a specific application window.

**Best for:**
- App demos
- Focused tutorials
- Clean recordings without desktop clutter

**How to use:**
1. Select **Window** mode
2. Click on the window you want to record
3. Click Record

**Note:** The window must remain visible during recording. Minimizing it will pause capture.

## Region Capture

Select any rectangular area of your screen.

**Best for:**
- Highlighting specific parts of your screen
- Recording a portion of a large display
- Privacy-conscious recording

**How to use:**
1. Select **Region** mode
2. Click and drag to define the capture area
3. Adjust the selection if needed
4. Click Record

## Resolution settings

Control the output resolution:

| Setting | Description |
|---------|-------------|
| Original | Records at your screen's native resolution |
| 1080p | Standard HD, good balance of quality and file size |
| 720p | Smaller files, ideal for quick shares |

## Frame rate

| Setting | Best for |
|---------|----------|
| 60 FPS | Fast-paced content, smooth motion |
| 30 FPS | Standard content, smaller files |
| Auto | Flare optimizes based on content |
        `,
      },
      {
        id: "camera-setup",
        title: "Camera & Webcam",
        content: `
# Camera & Webcam

Add a personal touch to your recordings with webcam overlay.

## Enabling the camera

1. Click the **camera icon** in the recording toolbar
2. Select your preferred camera from the dropdown
3. The preview shows what viewers will see

## Camera positions

Choose where your camera appears on screen:

| Position | When to use |
|----------|-------------|
| Bottom-right | Default, unobtrusive |
| Bottom-left | When content is on the right |
| Top-right | For certain layouts |
| Top-left | Matches some presentation styles |
| Custom | Drag to place anywhere |

## Camera shapes

- **Circle** — Clean, modern look (most popular)
- **Rectangle** — Traditional webcam style
- **Rounded Rectangle** — Balanced appearance

## Size adjustment

Use the slider to adjust camera size from 10% to 40% of screen width.

**Tip:** Smaller is often better. Your content should be the focus, not your face.

## Camera effects

### Background blur
Keeps focus on you by blurring your environment.
- Mild blur for subtle effect
- Strong blur for busy backgrounds

## Best practices

1. **Lighting** — Face a light source, not away from it
2. **Eye level** — Position camera at eye height when possible
3. **Framing** — Center yourself with some headroom
4. **Background** — Keep it clean and professional
5. **Distance** — Not too close, not too far
        `,
      },
      {
        id: "audio-setup",
        title: "Audio Recording",
        content: `
# Audio Recording

Great audio is essential for professional-quality videos.

## Audio sources

### Microphone
Records your voice.
- Select from available input devices
- Built-in mic, USB mic, or audio interface
- Adjust input volume to prevent clipping

### System Audio
Captures sounds from your computer.
- App sounds, music, notifications
- Perfect for software demos
- Shows viewers exactly what you hear

### Both
Combine microphone and system audio for complete recordings.

## Configuring audio

### Input level
Watch the level meter while speaking:
- **Green zone** — Good levels
- **Yellow zone** — Getting loud
- **Red zone** — Clipping (distorted audio)

Aim to speak in the green-to-yellow range.

### Noise suppression
AI-powered background noise reduction:
- **Off** — No processing
- **Low** — Light noise reduction
- **Medium** — Balanced (recommended)
- **High** — Aggressive filtering

## Best practices

### Environment
- Record in a quiet space
- Close windows and doors
- Turn off fans and AC if possible
- Soft surfaces (carpet, curtains) reduce echo

### Microphone technique
- Keep consistent distance from mic
- Speak clearly and at normal volume
- Avoid breathing directly into the mic
- Use a pop filter for plosives (p, b sounds)

### Before recording
- Do a quick test recording
- Listen back with headphones
- Adjust levels if needed
        `,
      },
    ],
  },
  {
    id: "editing",
    title: "Editing",
    articles: [
      {
        id: "editor-overview",
        title: "Editor Overview",
        content: `
# Editor Overview

The Flare editor gives you powerful tools to perfect your videos.

## Editor layout

### Preview area
The main view where you watch your video:
- Real-time preview of all edits
- Full-screen mode available
- Shows camera overlay positioning

### Timeline
The horizontal bar at the bottom:
- Visual representation of your video
- Drag to navigate through the video
- Shows audio waveform
- Displays edit points and cuts

### Sidebar
Tools panel on the right:
- **Camera** — Adjust overlay position and style
- **Audio** — Volume and audio settings
- **Canvas** — Background and layout options
- **Subtitles** — Caption controls

### Control bar
Playback controls above the timeline:
- Play/pause
- Skip forward/backward
- Current time / total duration
- Zoom controls

## Keyboard shortcuts

| Shortcut | Action |
|----------|--------|
| Space | Play / Pause |
| ← → | Move frame by frame |
| Shift + ← → | Move 0.5 seconds |
| C | Toggle split tool |
| Shift + C | Split at playhead |
| Cmd + Z | Undo |
| Cmd + Y | Redo |
| + / - | Zoom timeline |
| Esc | Deselect |

## Non-destructive editing

All edits in Flare are non-destructive:
- Your original recording is never modified
- Edits can be undone at any time
- Export creates a new file with your changes

## Auto-save

Changes are saved automatically as you work. You can close the editor and return later—your edits will be preserved.
        `,
      },
      {
        id: "trimming-cutting",
        title: "Trimming & Cutting",
        content: `
# Trimming & Cutting

Remove mistakes, long pauses, and unwanted sections from your videos.

## The split tool

The split tool lets you divide your video at any point.

### Activating split mode
- Press **C** to toggle split tool on/off
- Or click the scissors icon in the control bar

### Creating splits
With split mode active:
1. Position the playhead where you want to split
2. Click on the timeline
3. A split point is created

**Tip:** Press **Shift + C** to split at the current playhead position without enabling split mode.

## Trimming clips

After creating splits, you can trim sections:

1. Hover over the edge of a clip
2. Drag left or right to trim
3. The removed content is hidden (not deleted)

## Deleting sections

To remove a section entirely:

1. Create split points at the start and end of the section
2. Click on the section to select it
3. Press **Delete** or **Backspace**

The remaining clips automatically close the gap (ripple delete).

## Undo mistakes

Made a wrong cut? No problem:
- Press **Cmd + Z** to undo
- Press **Cmd + Y** to redo
- Full edit history is preserved

## Precision editing

For precise edits:
- Zoom in with **+** or the zoom slider
- Use **←** and **→** for frame-by-frame navigation
- Watch the timecode display for exact positions

## Best practices

1. **Watch first** — Play through your recording before editing
2. **Mark problem areas** — Note timestamps of sections to remove
3. **Work in order** — Edit from beginning to end
4. **Preview edits** — Always watch back your cuts
        `,
      },
      {
        id: "subtitles",
        title: "Subtitles & Captions",
        content: `
# Subtitles & Captions

Make your videos accessible with automatic subtitles.

## Generating subtitles

Flare uses AI to automatically transcribe your audio:

1. Open the **Subtitles** panel in the sidebar
2. Click **Generate Subtitles**
3. Wait for transcription (usually under a minute)
4. Review and edit the results

## Editing subtitles

### Edit text
Click on any subtitle segment to edit the text. Fix any transcription errors.

### Adjust timing
- Drag subtitle blocks left/right to adjust when they appear
- Drag edges to change duration
- Split or merge segments as needed

### Delete subtitles
Select a subtitle segment and press Delete to remove it.

## Styling options

Customize how subtitles appear:

| Option | Description |
|--------|-------------|
| Font size | Small, Medium, Large |
| Font color | Text color |
| Background | Color and opacity behind text |
| Position | Bottom, Top, or Custom |

## Export options

Choose how subtitles are included when exporting:

### Burned in (hardcoded)
Subtitles are permanently part of the video. Viewers can't turn them off.

**Pros:** Works everywhere, no player support needed
**Cons:** Can't be disabled, harder to update

### Separate file (SRT)
Subtitles exported as a separate .srt file.

**Pros:** Can be toggled on/off, easy to edit
**Cons:** Requires player support

## Tips for accuracy

- Speak clearly when recording
- Minimize background noise
- Review auto-generated text carefully
- Keep sentences concise for better timing
        `,
      },
    ],
  },
  {
    id: "sharing",
    title: "Sharing & Export",
    articles: [
      {
        id: "share-links",
        title: "Share Links",
        content: `
# Share Links

Share your videos instantly with anyone using a link.

## Getting a share link

1. Open your video (from Library or after recording)
2. Click the **Copy link** button
3. The link is copied to your clipboard
4. Share via email, chat, social media, or anywhere

## What viewers see

When someone opens your link:
- Video plays in a clean, modern player
- Works on any device with a browser
- No download or account required
- Viewers can leave timestamped comments

## Link features

### Comments
Viewers can leave comments on your video:
- Regular comments
- Timestamped comments (linked to specific moments)
- Reply threads

### Analytics
Track how your video performs:
- View count
- Watch time and retention
- Geographic data

## Privacy

Your videos are **private by default**:
- Only people with the link can view
- Videos are not indexed by search engines
- You control who gets the link

## Embedding videos

Add your video to websites and blogs:

1. Open video settings
2. Click **Get Embed Code**
3. Copy the HTML snippet
4. Paste into your website

### Customizing embeds
- Choose player colors to match your brand
- Set autoplay behavior
- Configure controls visibility
- Adjust player dimensions
        `,
      },
      {
        id: "downloading",
        title: "Download & Export",
        content: `
# Download & Export

Export your videos for offline use, uploading elsewhere, or archiving.

## Export options

### Quick Export
Optimized settings for most use cases:
- Fast processing
- Good quality/size balance
- Recommended for sharing

### Custom Export
Fine-tune your export settings:
- Resolution (720p, 1080p, 4K)
- Quality level
- File format

## Resolution options

| Resolution | Best for |
|------------|----------|
| 720p | Quick shares, smaller files |
| 1080p | Standard HD, general use |
| 4K | Maximum quality, large files |

## Quality levels

| Level | File size | Quality |
|-------|-----------|---------|
| High | Largest | Best visual quality |
| Medium | Balanced | Good for most uses |
| Low | Smallest | Reduced quality |

## How to export

1. Open your video in the editor
2. Click **Export** in the header
3. Choose your settings
4. Click **Export Video**
5. Wait for processing
6. Download begins automatically

## Export time

Processing time depends on:
- Video duration
- Resolution and quality settings
- Current server load

Most videos under 10 minutes export in 1-2 minutes.

## File format

Flare exports to **MP4** format:
- Universal compatibility
- Plays on any device
- Supported by all major platforms
- Can be uploaded anywhere

## Tips

- Use **High quality** for important presentations
- Use **Medium** for most sharing
- Check the estimated file size before exporting
- Large files (>25MB) may not work with email attachments
        `,
      },
      {
        id: "password-protected-videos",
        title: "Password Protected Videos",
        content: `
# Password Protected Videos

<link rel="preconnect" href="https://flarefasthost.com" crossorigin>
<link rel="preconnect" href="https://videos.flare.link" crossorigin>
<style>flare-player[media-id="f2da2bc2-c1b7-473a-9f31-ab15881b7dc2"]:not(:defined){display:block;width:100%;max-width:3024px;aspect-ratio:3024 / 1964;background-color:#000;position:relative;overflow:hidden;}flare-player[media-id="f2da2bc2-c1b7-473a-9f31-ab15881b7dc2"]:not(:defined)::before{content:'';position:absolute;inset:-12px;background:url('https://videos.flare.link/f2da2bc2-c1b7-473a-9f31-ab15881b7dc2/thumbnail.jpg') center/cover no-repeat;filter:blur(6px);}</style>
<flare-player media-id="f2da2bc2-c1b7-473a-9f31-ab15881b7dc2" style="display:block;width:100%;max-width:3024px;aspect-ratio:3024 / 1964;"></flare-player>
<script type="module" async src="https://flarefasthost.com/embed-data/f2da2bc2-c1b7-473a-9f31-ab15881b7dc2.js"></script>
<script type="module" async src="https://flarefasthost.com/component.js"></script>

Add an extra layer of security to your shared videos by requiring a password to view them.

## What are password protected videos?

Password protection lets you restrict access to your videos so that only people who know the password can watch them. When someone opens your share link, they'll be prompted to enter the password before the video plays.

This is useful for:
- **Confidential presentations** — Share internal demos or product roadmaps externally without risk
- **Client deliverables** — Send work-in-progress videos to specific clients
- **Paid content** — Gate access to premium video content
- **Sensitive material** — Protect HR, legal, or financial recordings

## How to add a password

1. Open your video from the Library
2. Click the **Share** button
3. Toggle **Password protection** on
4. Enter a password of your choice
5. Click **Save**

Your share link remains the same — viewers will just see a password prompt before the video loads.

## Sharing a password protected video

When you share a password protected video:
- Send the **link** and the **password** separately for better security
- Consider using different channels (e.g., link via email, password via chat)
- Let recipients know the video is password protected so they expect the prompt

## What viewers experience

When a viewer opens a password protected link:
1. They see the video thumbnail with a password prompt
2. They enter the password
3. The video loads and plays normally
4. Their session is remembered so they don't need to re-enter the password on refresh

## Managing passwords

- **Change a password** — Open share settings and update the password field
- **Remove protection** — Toggle password protection off to make the video accessible to anyone with the link
- **Forgot the password?** — As the video owner, you can always view and change the password from your share settings

## Tips

- Use a strong, unique password for sensitive content
- Avoid using common words or easily guessable passwords
- Remember that password protection works alongside link privacy — only people with both the link and the password can access your video
- You can combine password protection with other sharing features like custom branding and domain restrictions
        `,
      },
      {
        id: "framer-autoplay",
        title: "Add the autoplay script to Framer",
        description: "Add Flare’s autoplay script in Framer’s Custom Code settings, with a copyable script and screenshots for every step.",
        content: `
# Add the autoplay script to Framer

Let viewers start your Flare video with sound by clicking or tapping your Framer page. If a muted preview is already playing, the video restarts from the beginning when the browser allows sound.

## Copy the autoplay script

This script is the same for every Flare account and video. Click **Copy script** below. You do not need to change the code or find it in the video settings.

\`\`\`html
<script async src="https://flarefasthost.com/autoplay.js"></script>
\`\`\`

Keep your existing Flare video embed on the page. Add this extra script once in Framer’s site settings; it works with videos that have **Autoplay on load** or a **loop thumbnail** enabled.

## 1. Open your Framer project

Open the project containing your Flare video embed.

![Your website open in the Framer editor](/help/framer-autoplay/01-open-framer.png "Open the Framer project you want to update. Click any screenshot to view it full size.")

## 2. Open Settings

Click the **Canvas** menu in the top-left corner, then choose **Settings**.

![The Canvas menu with Settings highlighted](/help/framer-autoplay/02-open-settings.png "Canvas → Settings.")

## 3. Open Custom Code

Under **Site Settings**, click **Code**. In the **Custom Code** area, click **Add Script**. If you already have scripts, use the **+** button to add another.

![Framer Site Settings with Code selected and the Add Script button](/help/framer-autoplay/03-custom-code.png "Select Code in the sidebar, then Add Script.")

## 4. Paste the script

Fill in the form with these settings:

| Setting | Value |
| --- | --- |
| Name | Flare autoplay |
| Placement | End of \`<body>\` |
| Page | All, or the pages containing your Flare embeds |
| Run | Once |
| Code | Paste the script copied above |

Click **Add** to save it. The script belongs here in **Custom Code**, rather than inside the video’s Embed component.

![The Add Script form filled with the Flare autoplay script](/help/framer-autoplay/04-add-script-example.png "Edited setup example showing the exact script, End of <body>, and Once.")

## 5. Check your Flare video settings

In Flare, open the video’s embed customization modal. Under **Appearance → Autoplay**, enable **Autoplay on load**, or use a **loop thumbnail**. Click **Publish** in Flare to save any video-setting changes.

When **Autoplay on load** is enabled, the **Using Framer?** info box beneath it links to this guide. You can copy the script at the top of this article, or from the **Embed code** tab (the **</>** icon) → **Advanced settings → Autoplay with sound on your website**.

![Annotated Flare modal pointing to the Embed code tab with the autoplay script](/help/framer-autoplay/05-flare-script-annotated.png "Annotated example: the </> icon opens Embed code. The script shown here is the same one you can copy above.")

## 6. Publish your Framer site and test

Click **Publish** in Framer. Open the published website in a fresh browser tab to test the setup.

1. Let the muted video or loop preview start.
2. Click or tap the page outside the video.
3. When the browser permits it, the video restarts from the beginning with sound.
4. Further page interactions will not keep restarting a video that has successfully started with sound.

Test on the published website, rather than only in the Framer editor.

## If the video stays muted

- Check that you added the script in **Site Settings → Code**, saved it, and published your Framer site.
- Make sure **Page** includes the page you are testing and the Flare video has autoplay or a loop thumbnail enabled.
- Click or tap to test. Scrolling, hovering, and moving the mouse do not unlock audio.
- Some browsers and device settings still require a tap on the video itself. Keep the player’s play and unmute controls available.

For Framer’s general instructions, see [How to add custom code](https://www.framer.com/help/articles/how-to-add-custom-code/).
        `,
      },
      {
        id: "embed-domain",
        title: "Why embeds load from flarefasthost.com",
        content: `
# Why embeds load from flarefasthost.com

When you copy a Flare embed snippet, the script and iframe load from **flarefasthost.com**. This is intentional and helps your videos play reliably for more viewers.

## Why a different domain?

Public networks — airport Wi-Fi, hotel Wi-Fi, campus networks, corporate guest Wi-Fi — increasingly run aggressive firewall filtering. Newer top-level domains (like .link) sometimes get blanket-blocked by these filters because the TLD itself is younger and has accumulated less reputation than .com.

## Is the video itself any different?

No. flarefasthost.com is the same delivery edge:

- **Same performance** — bytes come from the same cache locations
- **Same player** — every feature works identically
- **Same analytics** — events still flow back to your dashboard

The only thing that changed is the hostname on the wire.

## Do I need to update old embeds?

We recommend switching existing embeds to flarefasthost.com when convenient, but old embeds pointing at embed.flare.link will still work. New embeds you copy from the dashboard will use flarefasthost.com going forward.
        `,
      },
      {
        id: "embed-standard-vs-fallback",
        title: "Standard vs Fallback embed mode",
        content: `
# Standard vs Fallback embed mode

When you copy a Flare embed snippet, you can pick between two delivery modes — **Standard** and **Fallback**. They both play the same video, but they render very differently.

## Standard (recommended)

Standard mode loads the player **inline on your page** as a custom HTML element (\`<flare-player>\`). The player runtime loads once per page, then renders directly into your page's DOM.

**Why it's the default:**
- **Faster first paint** — no nested document, no second JavaScript context to initialize
- **Lighter on the page** — multiple embeds on the same page share a single player runtime
- **Better integration** — the player sits in your page's DOM, so layout, fonts, and dark/light theming flow through naturally
- **Smoother on mobile** — fewer document parses means less main-thread work and lower battery use

If you have **multiple Flare videos on the same page**, Standard is strongly recommended. The runtime is fetched once and reused across every embed. With Fallback (iframe), each video is its own document, and the player runtime gets parsed and initialized once per video.

## Fallback (iframe)

Fallback mode wraps the player in an iframe. Each embed is its own isolated document.

**When to use it:**
- **Strict content security policies** — some CSPs block inline scripts or custom elements but allow iframes from a known origin
- **Sandboxed or unknown host environments** — CMSs, email-rendered previews, and widget areas with restricted permissions
- **Hostile CSS** — when the surrounding page has aggressive styles you can't control, an iframe gives you a clean rendering boundary

**The cost:**
Each iframe is a full page load: its own JavaScript heap, its own cache, its own decoder context. Two embeds = two parallel document parses. Five embeds = five. The bytes-on-the-wire are similar, but the CPU and memory cost compounds quickly on mobile.

## Which should I pick?

| Situation | Standard | Fallback |
| --- | --- | --- |
| One embed on a normal page | ✅ Best | OK |
| Multiple embeds on the same page | ✅ Strongly recommended | ⚠️ Will be slow |
| Page has a strict CSP that blocks inline scripts | ⚠️ May not load | ✅ Works |
| Embedding inside a sandboxed widget area | — | ✅ Required |

Default to Standard. Switch to Fallback only when you've actually hit a compatibility issue, or when the host page mandates iframe-only embeds.
        `,
      },
    ],
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    articles: [
      {
        id: "permission-issues",
        title: "Permission Issues",
        content: `
# Permission Issues

Flare needs certain macOS permissions to function properly.

## Screen Recording permission

**Symptom:** Black screen when recording, or "Screen recording not available"

**Fix:**
1. Open **System Settings** (or System Preferences)
2. Go to **Privacy & Security**
3. Select **Screen Recording** in the left sidebar
4. Find **Flare** in the list
5. Toggle it **ON**
6. **Restart Flare** (required for changes to take effect)

## Microphone permission

**Symptom:** No audio recorded, or microphone options are grayed out

**Fix:**
1. Open **System Settings**
2. Go to **Privacy & Security**
3. Select **Microphone**
4. Toggle **Flare** ON
5. Restart Flare

## Camera permission

**Symptom:** Camera preview is black, or camera toggle doesn't work

**Fix:**
1. Open **System Settings**
2. Go to **Privacy & Security**
3. Select **Camera**
4. Toggle **Flare** ON
5. Restart Flare

## Permission denied after granting

If permissions seem to be granted but still don't work:

1. Remove Flare from the permission list
2. Restart Flare
3. Grant the permission again when prompted
4. Restart Flare once more

## Permissions not appearing

If Flare doesn't appear in the permissions list:

1. Make sure you've launched Flare at least once
2. Try triggering the permission (e.g., start a recording)
3. The permission prompt should appear
4. If not, manually add Flare using the **+** button

## Still having issues?

- Restart your Mac
- Reinstall Flare
- Contact support with details about your macOS version
        `,
      },
      {
        id: "performance-issues",
        title: "Performance Issues",
        content: `
# Performance Issues

Optimize Flare for smooth recording and editing.

## Recording performance

### High CPU usage
- Close unnecessary applications
- Lower recording resolution to 1080p
- Reduce frame rate to 30 FPS
- Disable camera if not needed

### Dropped frames
- Free up disk space (need 10GB+ free)
- Record to an SSD, not external HDD
- Close browser tabs
- Quit background apps

### Choppy preview
- Ensure hardware acceleration is enabled
- Update macOS to latest version
- Restart Flare

## Editing performance

### Slow timeline
- Zoom out for better performance
- Close other applications
- Wait for waveform to fully load

### Laggy playback
- Lower preview quality temporarily
- Close sidebar panels you're not using
- Restart the editor

## Export performance

### Export taking too long
- Lower export quality
- Close background applications
- Ensure sufficient disk space
- Check internet connection (for cloud processing)

### Export failing
- Free up disk space
- Try a lower resolution
- Restart Flare and try again
- Contact support if persistent

## System recommendations

For best performance:

| Component | Recommendation |
|-----------|---------------|
| RAM | 8GB or more |
| Storage | SSD with 20GB+ free |
| Processor | Apple Silicon (M1/M2/M3) |
| macOS | Latest version |

## Energy settings

For laptop users:
- Keep charger connected when recording
- Disable "Low Power Mode"
- Prevent sleep during long exports
        `,
      },
      {
        id: "unusual-aspect-ratio",
        title: "Unusual Aspect Ratio",
        content: `
# Unusual Aspect Ratio

Some recordings have an aspect ratio that is wider (or narrower) than standard 16:9. This page explains how Flare handles these cases.

## What counts as unusual?

Any video significantly wider than 16:9 — for example 21:9 ultrawide monitors, 32:9 super-ultrawide displays, or multi-monitor recordings — is considered an unusual aspect ratio. Very tall or narrow recordings (such as portrait mobile captures taller than 9:16) also fall into this category.

## What changes for unusual aspect ratios?

When an unusual aspect ratio is detected, Flare may automatically adjust or hide certain player controls to keep the viewing experience clean:

- **Speed selector** — On mobile devices and in short embed containers, the overlay speed selector may be hidden to prevent it from overflowing beneath the control bar.
- **Overlay buttons** — The "Click to unmute" and play button overlays may be repositioned or simplified so they fit within the available space.

These adjustments only affect the embed player overlay controls. The bottom control bar (play/pause, timeline, volume, etc.) is always available regardless of aspect ratio.

## Why is this necessary?

Ultra-wide videos produce very short containers relative to their width. When centred overlay elements — like the speed selector or unmute button — are taller than the available space, they overlap with the bottom controls or extend outside the player. Hiding them automatically avoids this.

## Can I override this?

The adjustments are automatic and based on the container dimensions at playback time. If you resize the embed or view the video in a taller container (for example, by increasing the embed height), the hidden controls may reappear.

## Need help?

If your video's controls look unexpected, [contact support](/support) and include a link to the video so we can take a look.
        `,
      },
      {
        id: "upload-issues",
        title: "Upload Issues",
        content: `
# Upload Issues

Troubleshoot problems with video uploads.

## Upload fails immediately

### Check your connection
- Verify internet is working
- Try loading a website
- Restart your router if needed

### Check authentication
- Sign out and sign back in
- Your session may have expired

### File size
- Maximum file size: 5GB
- Try a shorter video or lower quality

## Upload stuck or slow

### Slow progress
Upload speed depends on your internet:
- 10 Mbps upload = ~1 minute per 75MB
- Large files take longer
- Don't close the window

### Progress stops
If upload progress freezes:
1. Wait 2-3 minutes (may be processing)
2. If still stuck, refresh the page
3. Try uploading again

### Very large files
For files over 2GB:
- Consider splitting into parts
- Export at lower quality
- Upload during off-peak hours

## Browser issues

### Clear cache
- Clear browser cache and cookies
- Refresh the page
- Try again

### Try different browser
- Chrome, Firefox, Safari all work
- Disable browser extensions temporarily
- Use incognito/private mode

## After upload

### Video not appearing
- Refresh your library
- Wait 1-2 minutes for processing
- Check the upload completed successfully

### Processing stuck
If video shows "Processing" for over 10 minutes:
- Contact support with the video ID
- May need to re-upload

## Prevention tips

1. Use a stable internet connection
2. Don't close the browser during upload
3. Upload shorter videos when possible
4. Keep files under 2GB for reliability
        `,
      },
    ],
  },
  {
    id: "billing",
    title: "Billing & Pricing",
    articles: [
      {
        id: "pricing-explained",
        title: "Pricing & VAT Explained",
        content: `
# Pricing & VAT Explained

Understanding how Flare pricing and taxes work.

## Our pricing

Flare is sold as two products:

| Product | Plan | Monthly | Yearly |
|---------|------|---------|--------|
| Record & Share | Recording | €8/mo | €72/yr |
| Embeds | Starter | $19/mo | $182/yr |
| Embeds | Pro | $59/mo | $566/yr |
| Embeds | Ultra | $179/mo | $1,718/yr |

Recording includes 200 GB storage, 4K recording, and unlimited duration. Embeds plans include hosted videos, bandwidth, analytics, and player customization. Prices can be shown in EUR, USD, GBP, AUD, or CAD depending on your billing country. Recording yearly is about 25% off monthly billing, and paid Embeds yearly plans are about 20% off monthly billing.

## How taxes work

Taxes are calculated based on your **billing address country**, not your shipping address.

### European Union (EU)

If you're in the EU, VAT (Value Added Tax) is added at checkout. VAT rates vary by country:

| Country | VAT Rate |
|---------|----------|
| Germany | 19% |
| France | 20% |
| Netherlands | 21% |
| Denmark | 25% |
| Sweden | 25% |

The exact rate depends on your billing country.

### United States

For US customers, we display prices in USD. Sales tax handling varies by state.

### Australia

Australian customers pay 10% GST (Goods and Services Tax) on top of the displayed price.

### Other countries

For EEA countries (Iceland, Norway, Liechtenstein), local VAT rates apply.

## VAT for businesses (EU only)

If you're a registered business in the EU with a valid VAT number, you may qualify for **VAT reverse charge**.

### What is reverse charge?

Reverse charge means you pay 0% VAT on your Flare subscription. Instead, you account for the VAT yourself in your business tax return.

### How to apply reverse charge

1. At checkout, check "I'm purchasing for a business"
2. Enter your company name
3. Enter your EU VAT number (e.g., DE123456789)
4. We automatically verify your VAT number with the EU VIES system
5. If valid, 0% VAT is applied

### Requirements

- You must have a valid, active EU VAT number
- Your VAT number must be registered with your country's tax authority
- The VAT number must be verifiable through the EU VIES database

### Verification failed?

If your VAT number doesn't validate:
- Double-check the format (country code + numbers)
- Ensure your VAT registration is active
- Contact your tax authority if recently registered
- You can still purchase as a consumer with VAT included

## Invoices

After each payment, you'll receive an invoice by email. Invoices include:
- Your billing details
- Plan and pricing information
- VAT amount and rate (if applicable)
- VAT number (if provided)

You can also download invoices from your [Profile](/profile) page.

## Currency

We charge in the currency of your billing country:
- **EUR** — European Union & EEA countries
- **USD** — United States
- **AUD** — Australia

Your bank may apply currency conversion if your account is in a different currency.

## Questions?

Contact our support team at support@getflare.io for billing inquiries.
        `,
      },
    ],
  },
  {
    id: "transcription",
    title: "Transcription",
    articles: [
      {
        id: "transcription-languages",
        title: "Adding Transcription Languages",
        content: `
# Adding Transcription Languages

Flare uses Apple's built-in speech recognition engine (the same technology behind Siri and Dictation) to generate real-time transcriptions of your recordings. This means transcription runs entirely on your Mac — fast, private, and works offline.

## How it works

When you enable **Auto-transcription** in Settings > Recording, Flare listens to your microphone audio during recording and generates word-by-word subtitles automatically. These subtitles appear on your video as soon as the recording is finished.

The quality and availability of transcription depends on which speech recognition models are installed on your Mac.

## Default language

Flare defaults to **English (US)** which is pre-installed on every Mac. You can change the active transcription language in **Settings > Languages**.

## Adding more languages

Since Flare uses Apple's speech engine, additional language models are managed through macOS System Settings:

1. Open **System Settings** on your Mac
2. Go to **Apple Intelligence & Siri** (or **Siri & Spotlight** on older macOS versions)
3. Click **Language** or **Dictation Languages**
4. Download the languages you need

Each language model is roughly **40–80 MB**. Once downloaded, it will appear as an on-device language in Flare's Languages settings.

## On-device vs. cloud

In Flare's Languages settings, you'll see two categories:

- **On-device** — These languages have a downloaded model on your Mac. Transcription is fast, private, and works without internet.
- **Cloud** — These languages are supported but don't have a local model. Transcription requires an internet connection and may be slower.

We recommend using on-device languages whenever possible for the best experience.

## Supported languages

Apple supports over 60 languages for speech recognition, including:

- English (US, UK, Australia, Canada, and more)
- Spanish, French, German, Italian, Portuguese
- Chinese (Mandarin, Cantonese), Japanese, Korean
- Danish, Swedish, Norwegian, Finnish, Dutch
- Arabic, Hebrew, Turkish, Hindi, Thai
- And many more

The full list is visible in Flare's **Settings > Languages** tab.
        `,
      },
    ],
  },
];
